import { injectable, inject } from 'inversify';
import { LoginUseCase } from '../../../domain/usecases/AuthenticationUseCases';
import type { UserRepository } from '../../../domain/repositories/UserRepository';
import { LoginCredentials, UserSession } from '../../../domain/models';
import { TYPES } from '../../../di/types';

/**
 * Implementation of LoginUseCase
 * This contains the business logic for user authentication
 */
@injectable()
export class LoginUseCaseImpl implements LoginUseCase {
  constructor(
    @inject(TYPES.UserRepository) private userRepository: UserRepository
  ) {}

  async execute(credentials: LoginCredentials): Promise<UserSession> {
    try {
      // Validate credentials format
      this.validateCredentials(credentials);

      // Authenticate user through repository
      const userSession = await this.userRepository.authenticate(credentials);

      if (!userSession) {
        throw new Error('Invalid credentials');
      }

      // Update last login time
      await this.userRepository.updateLastLogin(userSession.user.id);

      return userSession;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  private validateCredentials(credentials: LoginCredentials): void {
    if (!credentials.username || credentials.username.trim().length === 0) {
      throw new Error('Username is required');
    }

    if (!credentials.password || credentials.password.trim().length === 0) {
      throw new Error('Password is required');
    }

    // if (!credentials.branchId || credentials.branchId.trim().length === 0) {
    //   throw new Error('Branch selection is required');
    // }

    // Validate username format
    const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
    if (!usernameRegex.test(credentials.username)) {
      throw new Error('Username must be 3-20 characters and contain only letters, numbers, and underscores');
    }

    // Validate password length
    if (credentials.password.length < 6 || credentials.password.length > 50) {
      throw new Error('Password must be between 6 and 50 characters');
    }
  }
} 